import { useState, useEffect, useCallback } from 'react';
import { Content } from '../useContentData';
import { supabase } from '@/lib/supabase';

interface UseContentVersionProps {
    content: Content;
    versionNumber?: number;
}

/**
 * Hook to fetch a specific version of content
 * If no version number is provided, returns the current content
 */
export function useContentVersion({ content, versionNumber }: UseContentVersionProps) {
    const [versionedContent, setVersionedContent] = useState<Content | null>(content);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [lastFetchedVersion, setLastFetchedVersion] = useState<number | null>(null);

    // Function to fetch a specific version
    const fetchVersion = useCallback(async (contentId: string, version: number) => {
        try {
            setIsLoading(true);
            setError(null);

            // Fetch the specific version
            const { data, error } = await supabase
                .from('content_versions')
                .select('content_json, name')
                .eq('content_id', contentId)
                .eq('version_number', version)
                .single();

            if (error) {
                throw new Error(`Error fetching version: ${error.message}`);
            }

            if (!data) {
                throw new Error(`Version ${version} not found`);
            }

            // Create a new content object with the version data
            setVersionedContent({
                ...content,
                content_json: data.content_json,
                name: data.name,
                version_number: version
            });

            setLastFetchedVersion(version);
        } catch (err) {
            console.error('Error fetching content version:', err);
            setError(err instanceof Error ? err : new Error(String(err)));
        } finally {
            setIsLoading(false);
        }
    }, [content]);

    // Function to fetch the latest version (current_version)
    const fetchLatestVersion = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);

            // Fetch the current version from the content table to ensure we have the most up-to-date version
            const { data: contentData, error: contentError } = await supabase
                .from('contents')
                .select('current_version')
                .eq('id', content.id)
                .single();

            if (contentError) {
                throw new Error(`Error fetching content: ${contentError.message}`);
            }

            if (!contentData) {
                throw new Error(`Content not found`);
            }

            const currentVersion = contentData.current_version;

            // If the current_version differs from content.current_version, 
            // we need to fetch the latest version content
            if (currentVersion !== content.current_version) {
                await fetchVersion(content.id, currentVersion);
            } else {
                // If the version hasn't changed, just use the content that was passed in
                setVersionedContent({
                    ...content,
                    version_number: currentVersion
                });
                setLastFetchedVersion(currentVersion);
            }
        } catch (err) {
            console.error('Error fetching latest content version:', err);
            setError(err instanceof Error ? err : new Error(String(err)));
            // Fall back to the content passed in
            setVersionedContent(content);
        } finally {
            setIsLoading(false);
        }
    }, [content, fetchVersion]);

    // Function to manually refresh the version data
    const refreshVersion = useCallback(() => {
        if (versionNumber) {
            // If viewing a specific version, fetch that version
            fetchVersion(content.id, versionNumber);
        } else {
            // Otherwise fetch the latest version
            fetchLatestVersion();
        }
    }, [content.id, versionNumber, fetchVersion, fetchLatestVersion]);

    useEffect(() => {
        // If no version number is specified, use the latest content
        if (!versionNumber) {
            // For the initial render, set the content directly
            setVersionedContent(content);
            
            // Then check if we need to fetch the latest content
            if (content.current_version !== lastFetchedVersion) {
                fetchLatestVersion();
            }
            return;
        }

        // If version number does not match last fetched version, fetch it
        if (versionNumber !== lastFetchedVersion) {
            fetchVersion(content.id, versionNumber);
        }
    }, [content, versionNumber, lastFetchedVersion, fetchVersion, fetchLatestVersion]);

    return { versionedContent, isLoading, error, refreshVersion };
}

export default useContentVersion; 